import React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Field, FieldArrayRenderProps } from "@progress/kendo-react-form";
import { Typography } from "@progress/kendo-react-common";
import { MdDelete } from "react-icons/md";
import IconButton from "../../components/common/IconButton";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { Dialog } from "@progress/kendo-react-dialogs";

const SplitItemChildArray = (fieldArrayRenderProps: FieldArrayRenderProps) => {
  const [deleteIndex, setDeleteIndex] = React.useState<number | null>(null);
  const { splitIndex, parentFieldName } = fieldArrayRenderProps;
  const SplitItemList =
    fieldArrayRenderProps?.formRenderProps.valueGetter("ORDERLabeling");
  const fieldName = fieldArrayRenderProps.name;

  const calculateTotal = (weight: number, rate: number) => {
    return (weight || 0) * (rate || 0);
  };

  const pushElementInarray = React.useCallback(() => {
    fieldArrayRenderProps?.onPush({
      value: {
        ItemID: "",
        Weight: 0,
        Pcs: 0,
        CustomerRate: 0,
        Total: 0,
      },
    });
  }, [fieldArrayRenderProps]);

  const updateSplitWeightTotals = () => {
    const childItems =
      fieldArrayRenderProps.formRenderProps.valueGetter(`${fieldName}`) || [];
    const lessWeight = childItems.reduce(
      (sum: number, item: any) => sum + (Number(item.Weight) || 0),
      0,
    );
    const grossWeight = Number(
      fieldArrayRenderProps.formRenderProps.valueGetter(
        `${parentFieldName}.${splitIndex}.GrossWeight`,
      ) || 0,
    );
    const netWeight = grossWeight - lessWeight;

    fieldArrayRenderProps.formRenderProps.onChange(
      `${parentFieldName}.${splitIndex}.LessWeight`,
      { value: lessWeight },
    );
    fieldArrayRenderProps.formRenderProps.onChange(
      `${parentFieldName}.${splitIndex}.NetWeight`,
      { value: netWeight },
    );
  };

  return (
    <div className="border p-3 rounded bg-light mt-2 mb-3">
      {deleteIndex !== null && (
        <Dialog onClose={() => setDeleteIndex(null)}>
          <Typography.h5>{"Delete Child Item"}</Typography.h5>
          <GridLayout>
            <GridLayoutItem>
              <Typography.p>
                Are you sure you want to delete this child item?
              </Typography.p>
            </GridLayoutItem>
            <GridLayoutItem
              style={{
                display: "flex",
                justifyContent: "end",
              }}
            >
              <Button
                style={{ marginRight: 4 }}
                fillMode={"outline"}
                themeColor={"primary"}
                onClick={() => setDeleteIndex(null)}
                type="button"
              >
                Cancel
              </Button>
              <Button
                fillMode={"solid"}
                themeColor={"error"}
                onClick={() => {
                  fieldArrayRenderProps.onRemove({ index: deleteIndex });
                  setTimeout(() => {
                    updateSplitWeightTotals();
                  }, 50);
                  setDeleteIndex(null);
                }}
                type="button"
              >
                Delete
              </Button>
            </GridLayoutItem>
          </GridLayout>
        </Dialog>
      )}

      <GridLayout cols={[{ width: "50%" }, { width: "50%" }]}>
        <GridLayoutItem>
          <Typography.h6 style={{ marginBottom: 5, fontWeight: "bold" }}>
            Split Items Details
          </Typography.h6>
        </GridLayoutItem>
        <GridLayoutItem style={{ textAlign: "left", marginLeft: "auto" }}>
          <Button
            fillMode={"solid"}
            themeColor={"primary"}
            onClick={pushElementInarray}
            type="button"
          >
            Add Item
          </Button>
        </GridLayoutItem>
      </GridLayout>

      {fieldArrayRenderProps.value &&
        fieldArrayRenderProps.value.length > 0 && (
          <div
            className="table-responsive"
            style={{
              maxHeight: "30vh",
              scrollbarWidth: "thin",
              marginTop: 10,
            }}
          >
            <table
              className="table table-striped table-bordered table-hover field-array-table bg-white"
              style={{ minWidth: "100%" }}
            >
              <thead>
                <tr>
                  <th style={{ width: "35%" }}>Product Name</th>
                  <th style={{ width: "15%" }}>Weight (gm)</th>
                  <th style={{ width: "10%" }}>Pcs</th>
                  <th style={{ width: "20%" }}>Customer Rate (₹)</th>
                  <th style={{ width: "15%" }}>Total (₹)</th>
                  <th style={{ width: "5%" }}></th>
                </tr>
              </thead>
              <tbody>
                {fieldArrayRenderProps.value.map(
                  (labelItem: any, index: number) => (
                    <tr key={index}>
                      <td>
                        <Field
                          wrapperStyle={{ margin: 0 }}
                          name={`${fieldName}.${index}.ItemID`}
                          placeholder="Enter Product Name"
                          component={FormSelectionField}
                          validator={requiredValidator}
                          options={
                            SplitItemList?.length > 0
                              ? SplitItemList?.filter((item: any) => {
                                  const isSelectedElsewhere = fieldArrayRenderProps.value?.some(
                                    (val: any, valIdx: number) =>
                                      valIdx !== index && +val.ItemID === +item.ItemID
                                  );
                                  return !isSelectedElsewhere;
                                }).map((item: any) => ({
                                  value: item.ItemID,
                                  label: item.ItemName,
                                }))
                              : []
                          }
                        />
                      </td>
                      <td>
                        <Field
                          wrapperStyle={{ margin: 0 }}
                          name={`${fieldName}.${index}.Weight`}
                          placeholder="0.000"
                          type="number"
                          format="n3"
                          component={FormNumericTextField}
                          validator={requiredValidator}
                          onChange={(e: any) => {
                            const weight = e.value || 0;
                            const rate = Number(
                              fieldArrayRenderProps.formRenderProps.valueGetter(
                                `${fieldName}.${index}.CustomerRate`,
                              ) || 0,
                            );
                            const total = calculateTotal(weight, rate);
                            fieldArrayRenderProps.formRenderProps.onChange(
                              `${fieldName}.${index}.Total`,
                              { value: total },
                            );
                            // Trigger async updates
                            setTimeout(() => {
                              updateSplitWeightTotals();
                            }, 50);
                          }}
                        />
                      </td>
                      <td>
                        <Field
                          wrapperStyle={{ margin: 0 }}
                          name={`${fieldName}.${index}.Pcs`}
                          placeholder="0"
                          format="n0"
                          component={FormNumericTextField}
                          validator={requiredValidator}
                        />
                      </td>
                      <td>
                        <Field
                          wrapperStyle={{ margin: 0 }}
                          name={`${fieldName}.${index}.CustomerRate`}
                          placeholder="0.00"
                          type="number"
                          format="n2"
                          component={FormNumericTextField}
                          onChange={(e: any) => {
                            const rate = e.value || 0;
                            const weight = Number(
                              fieldArrayRenderProps.formRenderProps.valueGetter(
                                `${fieldName}.${index}.Weight`,
                              ) || 0,
                            );
                            const total = calculateTotal(weight, rate);
                            fieldArrayRenderProps.formRenderProps.onChange(
                              `${fieldName}.${index}.Total`,
                              { value: total },
                            );
                          }}
                        />
                      </td>
                      <td>
                        <Field
                          wrapperStyle={{ margin: 0 }}
                          name={`${fieldName}.${index}.Total`}
                          placeholder="0.00"
                          type="number"
                          format="n2"
                          component={FormNumericTextField}
                          disabled={true}
                        />
                      </td>
                      <td className="text-center">
                        <IconButton
                          type="button"
                          fillMode="solid"
                          size="small"
                          title="Delete"
                          themeColor="error"
                          onClick={() => setDeleteIndex(index)}
                        >
                          <MdDelete
                            className="absolute-position"
                            style={{ fontSize: "22px" }}
                          />
                        </IconButton>
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
};

export default SplitItemChildArray;
